import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiWithoutCamelization } from "../../../api";
import { useToast } from "../../../mantine/notifications/useToast";
import urls from "../../../urls";
import type {
  DirektvermarktungGeneratorShareFormValues,
  DirektvermarktungGeneratorShareResponse
} from "../Direktvermarktung.types";
import { direktvermarktungContractQueries } from "./useDirektvermarktungContract";
import type { UseDirektvermarktungUnassignedGeneratorsProps } from "./useDirektvermarktungUnassignedGenerators";

interface CreateMutationData {
  formValues: DirektvermarktungGeneratorShareFormValues;
  contractId: string;
}

export function useDirektvermarktungGeneratorShareMutations(
  contractInfo: UseDirektvermarktungUnassignedGeneratorsProps
) {
  const queryClient = useQueryClient();
  const { showErrorToast } = useToast();

  const addGeneratorShareToVermarktungContractMutation = useMutation({
    mutationFn: (data: CreateMutationData) =>
      addGeneratorShareToVermarktungContract(data.formValues, data.contractId),
    onSuccess: handleSuccess,
    onError: showErrorToast
  });

  const removeGeneratorShareFromVermarktungContractMutation = useMutation({
    mutationFn: (generatorId: number) =>
      removeGeneratorShareFromVermarktungContract(
        contractInfo.contractUuid,
        generatorId
      ),
    onSuccess: handleSuccess,
    onError: showErrorToast
  });

  async function handleSuccess() {
    return Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["direktvermarktungUnassignedGenerators", contractInfo]
      }),
      queryClient.invalidateQueries(
        direktvermarktungContractQueries.detail(contractInfo.contractUuid)
      ),
      queryClient.invalidateQueries({
        queryKey: ["siteWizard"]
      })
    ]);
  }

  return {
    addGeneratorShareToVermarktungContractMutation,
    removeGeneratorShareFromVermarktungContractMutation
  };
}

function addGeneratorShareToVermarktungContract(
  formValues: DirektvermarktungGeneratorShareFormValues,
  contractId: string
) {
  const url = urls.apiWithoutCamelization.direktvermarktungGeneratorShare(
    contractId,
    formValues.generator_id
  );
  return apiWithoutCamelization.patch<DirektvermarktungGeneratorShareResponse>(
    url,
    {
      ...formValues,
      contract_id: contractId
    }
  );
}

function removeGeneratorShareFromVermarktungContract(
  contractId: string,
  generatorId: number
) {
  const url = urls.apiWithoutCamelization.direktvermarktungGeneratorShare(
    contractId,
    generatorId
  );
  return apiWithoutCamelization.delete(url);
}
